import { Component, OnInit, Query } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { CheckboxModule } from 'primeng/checkbox';
import { TableModule } from 'primeng/table';
import { DropdownModule } from 'primeng/dropdown';
import { OrderListModule } from 'primeng/orderlist';
import { MessageService } from 'primeng/api';
import { EmailCampaignService, Campaign, CampaignCreateDto, CampaignUpdateDto, SendGridUtilService } from '@harlowe/core';
import { SendGridDynamicTemplate } from '../../../../../../core/src/lib/models/sendgrid-dynamic-template';

interface SequenceItem {
  uid: string;
  templateId: string;
  label: string;
  isOrphan: boolean;
}

const NO_MATCH_LABEL = 'NO MATCHING TEMPLATE';

@Component({
  selector: 'app-email-campaign-manager',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CardModule,
    DialogModule,
    InputTextModule,
    InputNumberModule,
    CheckboxModule,
    TableModule,
    DropdownModule,
    OrderListModule
  ],
  templateUrl: './email-campaign-manager.component.html',
  styleUrl: './email-campaign-manager.component.scss',
})
export class EmailCampaignManagerComponent implements OnInit {
  campaigns: Campaign[] = [];
  templates: SendGridDynamicTemplate[] = [];
  dialogVisible = false;
  isEditing = false;
  selectedCampaignId?: number;

  form = {
    numberOfEmails: 1,
    timeBetweenEmails: 1,
    sendGridTemplateIdSequence: [] as Array<string | number | boolean | null>,
    killSequenceOnResponse: false,
  };

  //for the ordered list
  templateIdToAdd: string | null = null;
  sequenceItems: SequenceItem[] = [];
  private uidCounter = 0;

  constructor(
    private readonly emailCampaignService: EmailCampaignService,
    private readonly sendGridUtilService: SendGridUtilService,
    private readonly messageService: MessageService,
  ) { }

  ngOnInit(): void {
    this.loadCampaigns();
    this.loadDynamicTemplates();
  }

  loadCampaigns(): void {
    this.emailCampaignService.getAll().subscribe({
      next: (campaigns) => {
        this.campaigns = campaigns;
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Unable to load campaigns' });
      },
    });
  }

  loadDynamicTemplates(): void {
    this.sendGridUtilService.getDynamicTemplates().subscribe({
      next: (templates) => {
        this.templates = templates;
        this.sequenceItems = this.sequenceItems.map((item) => this.resolveSequenceItem(item.templateId, item.uid));
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Unable to load dynamic templates from SendGrid' });
      }
    });
  }

  private nextUid(): string {
    this.uidCounter += 1;
    return `seq-${Date.now()}-${this.uidCounter}`;
  }

  private resolveSequenceItem(templateId: string, uid: string): SequenceItem {
    const match = this.templates.find((t) => t.id === templateId);
    return {
      uid,
      templateId,
      label: match ? match.name : NO_MATCH_LABEL,
      isOrphan: !match,
    };
  }

  addToSequence(): void {
    if (!this.templateIdToAdd) {
      return;
    }
    this.sequenceItems = [
      ...this.sequenceItems,
      this.resolveSequenceItem(this.templateIdToAdd, this.nextUid()),
    ];
  }

  removeFromSequence(item: SequenceItem): void {
    this.sequenceItems = this.sequenceItems.filter((i) => i.uid !== item.uid);
  }

  openCreateDialog(): void {
    this.isEditing = false;
    this.selectedCampaignId = undefined;
    this.form = {
      numberOfEmails: 1,
      timeBetweenEmails: 1,
      sendGridTemplateIdSequence: [],
      killSequenceOnResponse: false,
    };
    this.sequenceItems = [];
    this.templateIdToAdd = null;
    this.dialogVisible = true;
  }

  openEditDialog(campaign: Campaign): void {
    this.isEditing = true;
    this.selectedCampaignId = campaign.campaignId;
    this.form = {
      numberOfEmails: campaign.numberOfEmails,
      timeBetweenEmails: campaign.timeBetweenEmails,
      sendGridTemplateIdSequence: Array.isArray(campaign.sendGridTemplateIdSequence) 
        ? campaign.sendGridTemplateIdSequence as Array<string | number | boolean | null> 
        : [],
      killSequenceOnResponse: campaign.killSequenceOnResponse,
    };
    this.sequenceItems = this.form.sendGridTemplateIdSequence
      .filter((v) => v !== null && v !== undefined)
      .map((v) => this.resolveSequenceItem(String(v), this.nextUid()));

    this.templateIdToAdd = null;
    this.dialogVisible = true;
  }

  saveCampaign(): void {
    this.form.sendGridTemplateIdSequence = this.sequenceItems.map((item) => item.templateId);

    const dto = new CampaignCreateDto({
      numberOfEmails: this.form.numberOfEmails,
      timeBetweenEmails: this.form.timeBetweenEmails,
      sendGridTemplateIdSequence: this.form.sendGridTemplateIdSequence,
      killSequenceOnResponse: this.form.killSequenceOnResponse,
    });

    if (this.isEditing && this.selectedCampaignId !== undefined) {
      this.emailCampaignService.update(this.selectedCampaignId, new CampaignUpdateDto({
        numberOfEmails: dto.numberOfEmails,
        timeBetweenEmails: dto.timeBetweenEmails,
        sendGridTemplateIdSequence: dto.sendGridTemplateIdSequence,
        killSequenceOnResponse: dto.killSequenceOnResponse,
      })).subscribe({
        next: () => {
          this.dialogVisible = false;
          this.loadCampaigns();
        },
        error: () => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Unable to save campaign' });
        },
      });
      return;
    }

    this.emailCampaignService.create(dto).subscribe({
      next: () => {
        this.dialogVisible = false;
        this.loadCampaigns();
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Unable to save campaign' });
      },
    });
  }

  deleteCampaign(campaign: Campaign): void {
    this.emailCampaignService.delete(campaign.campaignId).subscribe({
      next: () => {
        this.loadCampaigns();
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Unable to delete campaign' });
      },
    });
  }

  private parseTemplateSequence(value: string): Array<string | number | boolean | null> {
    return value
      .split(',')
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0)
      .map((entry) => {
        if (entry === 'true') {
          return true;
        }

        if (entry === 'false') {
          return false;
        }

        if (/^-?\d+$/.test(entry)) {
          return Number(entry);
        }

        return entry;
      });
  }
}
